import React, { createContext, useState, useCallback, useMemo, useContext, useRef } from 'react';
import { componentService } from '../services/ComponentService';

export const ComponentManagerContext = createContext();

export const ComponentManagerProvider = props => {
    // Each level: { parentId, items: [] }. columns[0] holds the roots.
    const [columns, setColumns] = useState([]);
    // Root-to-active path; drives the breadcrumb.
    const [selectedPath, setSelectedPath] = useState([]);
    const [selectedComponent, setSelectedComponent] = useState(null);
    // Column currently fetching children, or null; drives the loader.
    const [loadingColumnIndex, setLoadingColumnIndex] = useState(null);

    // Token guarding out-of-order child fetches (last click wins).
    const requestToken = useRef(0);

    // Actions intentionally do NOT .catch() — rejections bubble to the page for toasts.

    const loadRoots = useCallback(() => {
        const token = (requestToken.current += 1);
        setSelectedPath([]);
        setSelectedComponent(null);
        setLoadingColumnIndex(null);
        setColumns([]);
        return componentService.getComponentRoots().then(res => {
            if (token !== requestToken.current) return;
            setColumns([{ parentId: null, items: res?.data ?? [] }]);
        });
    }, []);

    const selectComponent = useCallback((component, columnIndex) => {
        const token = (requestToken.current += 1);

        setSelectedComponent(component);
        setSelectedPath(prev => [...prev.slice(0, columnIndex), component]);

        if (!component.has_dependents) {
            setColumns(prev => prev.slice(0, columnIndex + 1));
            setLoadingColumnIndex(null);
            return Promise.resolve();
        }

        // Reuse already-loaded children when re-selecting a node (e.g. clicking a
        // breadcrumb you're already under) — avoids a destructive collapse + refetch.
        const existingChild = columns[columnIndex + 1];
        if (existingChild && existingChild.parentId === component.id) {
            setColumns(prev => prev.slice(0, columnIndex + 2));
            setLoadingColumnIndex(null);
            return Promise.resolve();
        }

        // Keep columns up to the clicked one and show a loader for the child column.
        setColumns(prev => prev.slice(0, columnIndex + 1));
        setLoadingColumnIndex(columnIndex + 1);

        return componentService
            .getComponentChildren(component.id)
            .then(res => {
                if (token !== requestToken.current) return;
                setColumns(prev => [
                    ...prev.slice(0, columnIndex + 1),
                    { parentId: component.id, items: res?.data ?? [] },
                ]);
                setLoadingColumnIndex(null);
            })
            .catch(err => {
                // Stale request: the user already moved on — swallow silently so
                // an abandoned slow click can't fire a phantom error toast.
                if (token !== requestToken.current) return;
                setLoadingColumnIndex(null);
                throw err;
            });
    }, [columns]);

    const selectAncestor = useCallback(index => {
        const ancestor = selectedPath[index];
        if (!ancestor) return Promise.resolve();
        return selectComponent(ancestor, index);
    }, [selectedPath, selectComponent]);

    const clearSelection = useCallback(() => {
        requestToken.current += 1;
        setSelectedPath([]);
        setSelectedComponent(null);
        setLoadingColumnIndex(null);
        setColumns(prev => prev.slice(0, 1));
    }, []);

    // These intentionally do NOT .catch() — rejections bubble to the caller
    // (PointsTab / ManagePointsModal) which surfaces toasts/messages.

    const refreshColumn = useCallback(parentId => {
        const token = (requestToken.current += 1);
        const fetch = parentId == null
            ? componentService.getComponentRoots()
            : componentService.getComponentChildren(parentId);
        return fetch.then(res => {
            if (token !== requestToken.current) return;
            const items = res?.data ?? [];
            setColumns(prev => {
                const idx = prev.findIndex(col => col.parentId === parentId);
                if (idx === -1) {
                    return [...prev, { parentId, items }];
                }
                const next = [...prev];
                next[idx] = { parentId, items };
                return next;
            });
        });
    }, []);

    const patchComponent = useCallback(updated => {
        setColumns(prev =>
            prev.map(col => ({
                ...col,
                items: col.items.map(item =>
                    item.id === updated.id ? { ...item, ...updated } : item
                ),
            }))
        );
        setSelectedPath(prev =>
            prev.map(node => (node && node.id === updated.id ? { ...node, ...updated } : node))
        );
        setSelectedComponent(prev =>
            prev && prev.id === updated.id ? { ...prev, ...updated } : prev
        );
    }, []);

    const handleComponentDeleted = useCallback(() => {
        const path = selectedPath;
        const len = path.length;
        const token = (requestToken.current += 1);

        if (len <= 1) {
            setSelectedPath([]);
            setSelectedComponent(null);
            setLoadingColumnIndex(null);
            return componentService.getComponentRoots().then(res => {
                if (token !== requestToken.current) return;
                setColumns([{ parentId: null, items: res?.data ?? [] }]);
            });
        }

        const parent = path[len - 2];
        const grandParentId = path[len - 3]?.id ?? null;
        const parentColIndex = len - 2;

        setSelectedComponent(parent);
        setSelectedPath(path.slice(0, len - 1));
        setLoadingColumnIndex(null);

        const fetchRow = grandParentId == null
            ? componentService.getComponentRoots()
            : componentService.getComponentChildren(grandParentId);

        return Promise.all([componentService.getComponentChildren(parent.id), fetchRow]).then(
            ([childrenRes, rowRes]) => {
                if (token !== requestToken.current) return;
                const children = childrenRes?.data ?? [];
                const rowItems = rowRes?.data ?? [];
                const freshParent =
                    rowItems.find(item => item.id === parent.id) ?? {
                        ...parent,
                        has_dependents: children.length > 0,
                    };

                setColumns(prev => {
                    const next = prev.slice(0, parentColIndex + 1);
                    const rowColIdx = next.findIndex(col => col.parentId === grandParentId);
                    if (rowColIdx !== -1) {
                        next[rowColIdx] = { parentId: grandParentId, items: rowItems };
                    }
                    if (children.length > 0) {
                        next.push({ parentId: parent.id, items: children });
                    }
                    return next;
                });
                setSelectedComponent(freshParent);
                setSelectedPath(path.slice(0, len - 2).concat(freshParent));
            }
        );
    }, [selectedPath]);

    const createComponent = useCallback(payload => {
        return componentService.createComponent(payload);
    }, []);

    const getComponentDeleteImpact = useCallback(id => {
        return componentService.getComponentDeleteImpact(id);
    }, []);

    const deleteComponent = useCallback(id => {
        return componentService.deleteComponent(id);
    }, []);

    const updateComponentDetails = useCallback(payload => {
        return componentService.updateComponentDetails(payload);
    }, []);

    const getComponentDetails = useCallback(id => {
        return componentService.getComponentDetails(id);
    }, []);

    const getComponentTypes = useCallback(() => {
        return componentService.getComponentTypes();
    }, []);

    const getComponentPoints = useCallback(componentId => {
        return componentService.getComponentPoints(componentId);
    }, []);

    const setComponentPoints = useCallback((componentId, genericPointIds) => {
        return componentService.setComponentPoints(componentId, genericPointIds);
    }, []);

    const deleteComponentPoint = useCallback(componentPointId => {
        return componentService.deleteComponentPoint(componentPointId);
    }, []);

    const moveComponentPoint = useCallback((componentPointId, newComponentId) => {
        return componentService.moveComponentPoint(componentPointId, newComponentId);
    }, []);

    const fetchComponentRoots = useCallback(() => {
        return componentService.getComponentRoots();
    }, []);

    const fetchComponentChildren = useCallback(parentId => {
        return componentService.getComponentChildren(parentId);
    }, []);

    const getGenericPoints = useCallback(params => {
        return componentService.getGenericPoints(params);
    }, []);

    const getPointDependents = useCallback(componentPointIds => {
        return componentService.getPointDependents(componentPointIds);
    }, []);

    const getComponentFunctions = useCallback(componentId => {
        return componentService.getComponentFunctions(componentId);
    }, []);

    const setComponentFunctions = useCallback((componentId, functionIds) => {
        return componentService.setComponentFunctions(componentId, functionIds);
    }, []);

    const getFunctions = useCallback(params => {
        return componentService.getFunctions(params);
    }, []);

    const values = useMemo(
        () => ({
            columns,
            selectedPath,
            selectedComponent,
            loadingColumnIndex,
            loadRoots,
            selectComponent,
            selectAncestor,
            clearSelection,
            refreshColumn,
            patchComponent,
            handleComponentDeleted,
            createComponent,
            getComponentDeleteImpact,
            deleteComponent,
            updateComponentDetails,
            getComponentDetails,
            getComponentTypes,
            getComponentPoints,
            setComponentPoints,
            deleteComponentPoint,
            moveComponentPoint,
            fetchComponentRoots,
            fetchComponentChildren,
            getGenericPoints,
            getPointDependents,
            getComponentFunctions,
            setComponentFunctions,
            getFunctions,
        }),
        [
            columns,
            selectedPath,
            selectedComponent,
            loadingColumnIndex,
            loadRoots,
            selectComponent,
            selectAncestor,
            clearSelection,
            refreshColumn,
            patchComponent,
            handleComponentDeleted,
            createComponent,
            getComponentDeleteImpact,
            deleteComponent,
            updateComponentDetails,
            getComponentDetails,
            getComponentTypes,
            getComponentPoints,
            setComponentPoints,
            deleteComponentPoint,
            moveComponentPoint,
            fetchComponentRoots,
            fetchComponentChildren,
            getGenericPoints,
            getPointDependents,
            getComponentFunctions,
            setComponentFunctions,
            getFunctions,
        ]
    );

    return (
        <ComponentManagerContext.Provider value={values}>
            {props.children}
        </ComponentManagerContext.Provider>
    );
};

export const useComponentManager = () => {
    const context = useContext(ComponentManagerContext);
    if (context === undefined) {
        throw new Error('useComponentManager must be used within a ComponentManagerProvider component.');
    }
    return context;
};
